import React, { useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { useNavigate } from "react-router-dom";
import "swiper/css";
import "swiper/css/navigation";
import Header from "../components/layout/Header";
import Footer from "./Footer";
import servicesHeroBanner from "../assets/suvidha-services-banner.png";

const tabs = [
  { id: "health",    name: "Health Support",    icon: "🏥" },
  { id: "equipment", name: "Medical Equipment", icon: "🩺" },
  { id: "lab",       name: "Lab & Diagnostics", icon: "🧪" },
  { id: "tele",      name: "Tele Consultation", icon: "📞" },
  { id: "tech",      name: "Elder Tech",        icon: "💻" },
  { id: "care",      name: "Home Health Care",  icon: "🧑‍⚕️" },
];

/* Line-icon set for the category rail — one consistent stroke style
   rather than emoji, which renders inconsistently across platforms. */
const Icon = ({ children }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
    strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

const ICONS = {
  health:    <Icon><path d="M3 12h4l3 8 4-16 3 8h4" /></Icon>,
  equipment: <Icon><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M12 11v6M9 14h6" /></Icon>,
  lab:       <Icon><path d="M9 3h6" /><path d="M10 3v6l-5 9a2 2 0 0 0 1.8 3h10.4a2 2 0 0 0 1.8-3l-5-9V3" /><path d="M7 14h10" /></Icon>,
  tele:      <Icon><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.12 4.18 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" /></Icon>,
  tech:      <Icon><rect x="7" y="7" width="10" height="10" rx="2" /><path d="M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3" /><path d="M9 17v3a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1v-3" /><path d="M12 10v2l1.5 1" /></Icon>,
  care:      <Icon><path d="M12 21s-7-4.35-9.5-9A5.5 5.5 0 0 1 12 6a5.5 5.5 0 0 1 9.5 6c-2.5 4.65-9.5 9-9.5 9z" /><path d="M12 10v4M10 12h4" /></Icon>,
};

/* Small icons used inside the expanded ticket detail */
const Chevron = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
    strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
);
const CheckDot = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M8 12.3l2.5 2.5L16 9.3" />
  </svg>
);
const InfoDot = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <path d="M12 7.5h.01" />
  </svg>
);

/* Operational notes shown in "Good to know" — general per-category facts,
   not specific to any one service. Placeholder language; swap for your
   team's actual process details whenever you have them. */
const CATEGORY_NOTES = {
  health:    ["Our care coordinator confirms details with you shortly after booking.", "Available across your registered service area.", "Reachable by phone or WhatsApp before and after the service."],
  equipment: ["Delivered and set up at your home by our team.", "Flexible daily or monthly rental plans available.", "Pickup scheduled automatically at the end of your rental period."],
  lab:       ["Sample collected at home by a trained phlebotomist.", "Reports shared digitally as soon as they're ready.", "All partner labs are NABL certified."],
  tele:      ["Consultation happens over a secure video call from your phone or laptop.", "A summary of the consultation is shared with you afterward.", "Reschedule up to a few hours before your slot at no extra charge."],
  tech:      ["Device ships with setup instructions and a short orientation call.", "Connects to the Suvidha app for ongoing monitoring.", "Support team on call if you need help getting started."],
  care:      ["Caregiver profile and experience shared with you before the visit.", "Regular updates keep family members informed.", "A replacement is arranged promptly if ever needed."],
};

const servicesData = {
  lab: [
    { title:"Full Body Check-up",        desc:"Comprehensive health screening with 80+ parameters for complete wellness assessment.", points:["80+ tests included","Home sample collection","Reports in 24 hours"],   price:"₹1,499", duration:"1 day" },
    { title:"CBC — Complete Blood Count", desc:"20-parameter blood analysis to detect infections, anaemia and blood disorders.",        points:["20 essential tests","Results in 6 hours","NABL certified labs"],      price:"₹299",   duration:"6 hours" },
    { title:"Anemia Profile (15 Tests)",  desc:"Detailed iron, B12 and folate panel to diagnose and monitor anaemia.",                  points:["15 targeted tests","Iron & B12 levels","Doctor review included"],    price:"₹549",   duration:"1 day" },
    { title:"Diabetes Screening",         desc:"HbA1c, fasting glucose and insulin resistance panel for diabetes risk assessment.",      points:["HbA1c + fasting sugar","3-month glucose trend","Diet counselling"],   price:"₹399",   duration:"Same day" },
  ],
  health: [
    { title:"Emergency Ambulance", desc:"24/7 emergency ambulance with trained paramedics and life-support equipment.", points:["Available 24/7","Trained paramedics","GPS tracked vehicle"], price:"From ₹999", duration:"Immediate",
      priceTiers: [
        { label:"1–5 km",                price:"₹999",   book:"Emergency Ambulance (1–5 km)" },
        { label:"5–10 km",               price:"₹1,499", book:"Emergency Ambulance (5–10 km)" },
        { label:"10 km & above",         price:"₹2,499", book:"Emergency Ambulance (10 km & above)" },
        { label:"Out of city (per km)",  price:"₹15/km" },
      ],
    },
    { title:"Planned Hospitalization", desc:"We coordinate the patient's pickup and hospital admission, keeping family and next of kin informed at every step.", points:["Patient pickup coordination","Family & next-of-kin updates","Admission support"], price:"₹999", duration:"Planned" },
    { title:"Health Insurance Assist", desc:"We recommend the best health insurance plans for senior citizens, based on independent market analysis.",         points:["Senior-citizen focused plans","Independent market comparison","Personalised recommendation"], price:"₹499", duration:"Consultation" },
  ],
  equipment: [
    { title:"Wheelchair Rental",    desc:"Premium manual and motorised wheelchairs for home use or hospital visits.",          points:["Daily & monthly plans","Home delivery & pickup","Sanitized before delivery"],  price:"₹250/day", duration:"Flexible" },
    { title:"Oxygen Concentrator",  desc:"Medical-grade oxygen concentrators delivered and set up at home by trained staff.",   points:["24hr availability","Setup by trained staff","Ongoing maintenance"],            price:"₹600/day", duration:"Flexible" },
    { title:"Hospital Bed Rental",  desc:"Adjustable hospital-grade beds for post-surgery recovery or long-term home care.",    points:["Manual & electric options","Mattress included","Free installation"],           price:"₹400/day", duration:"Flexible" },
  ],
  tele: [
    { title:"Video Consultation",     desc:"Connect with 500+ specialist doctors from the comfort of your home via video call.", points:["500+ specialists","Available 7 days","Digital prescriptions"],  price:"₹299", duration:"30 min" },
    { title:"Second Medical Opinion", desc:"Get an independent expert review of your diagnosis or treatment plan.",              points:["Board-certified experts","Detailed written report","100% confidential"], price:"₹799", duration:"48 hours" },
    { title:"Mental Health Support",  desc:"Confidential tele-therapy sessions with licensed psychologists and counsellors.",    points:["Licensed counsellors","Private & secure","Flexible scheduling"],   price:"₹499", duration:"45 min" },
  ],
  tech: [
    { title:"Emergency SOS Button",       desc:"One-touch wearable SOS device that alerts family and emergency services instantly.", points:["GPS enabled","24/7 monitoring centre","Instant family alerts"], price:"₹1,999", duration:"Device + 1yr plan" },
    { title:"Vitals Health Monitor",      desc:"Smart wearable tracking heart rate, SpO2, blood pressure and sleep patterns.",      points:["Heart rate & SpO2","Fall detection alert","Connected mobile app"], price:"₹3,499", duration:"Device only" },
    { title:"Medication Reminder System", desc:"Smart pill dispenser with auto-alerts and caregiver notifications for missed doses.", points:["Auto dose reminders","Caregiver alerts","Weekly medication log"],  price:"₹1,299", duration:"Device + setup" },
  ],
  care: [
    { title:"Trained Nurse at Home", desc:"Qualified RN nurses for post-surgery recovery, wound care or chronic illness management.", points:["RN certified nurses","Day or night shifts","Medication management"], price:"₹1,499/day", duration:"12 hr shift" },
    { title:"Physiotherapy at Home", desc:"In-home physiotherapy by licensed therapists with personalised recovery plans.",          points:["Licensed therapists","Custom recovery plan","Progress tracking"],    price:"₹799/session",duration:"1 hour" },
    { title:"Caregiver / Attendant", desc:"Trained and verified caregivers for elderly assistance, companionship and daily support.", points:["Background verified","Day/night availability","Weekly health reports"],price:"₹999/day", duration:"12 hr shift" },
  ],
};

/* `book` = exact name of the bookable service in the database.
   Cards with no true equivalent (Medicine Delivery, General Physician) have
   none: the booking page opens with the name searched and a short notice. */
const bookedServices = [
  { title:"Medicine Delivery",   image:"https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?q=80&w=600", category:"lab" },
  { title:"General Physician",   image:"https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?q=80&w=600", category:"tele" },
  { title:"Physiotherapy",       image:"https://images.unsplash.com/photo-1580281657527-47f249e8f4df?q=80&w=600", category:"care", book:"Physiotherapy at Home" },
  { title:"Nurse at Home",       image:"https://images.unsplash.com/photo-1581056771107-24ca5f033842?q=80&w=600", category:"care", book:"Trained Nurse at Home" },
  { title:"Ambulance Service",   image:"https://images.unsplash.com/photo-1593491205049-7f032d28cf5c?q=80&w=600", category:"health", book:"Emergency Ambulance" },
  { title:"Oxygen Concentrator", image:"https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?q=80&w=600", category:"equipment", book:"Oxygen Concentrator" },
  { title:"Health Monitoring",   image:"https://images.unsplash.com/photo-1576091160550-2173dba999ef?q=80&w=600", category:"tech", book:"Vitals Health Monitor" },
];

/* ═══════════════════════════════════════════════════ */
export default function ServicesPage() {
  const [active,   setActive]   = useState("lab");
  const [expanded, setExpanded] = useState(null);
  const navigate  = useNavigate();

  const selectCategory = (id) => { setActive(id); setExpanded(null); };
  const toggleExpand   = (key) => setExpanded(prev => prev === key ? null : key);

  // ── Login check + redirect ────────────────────────
  // The booking page matches by exact service name against the services
  // table (see scripts/seed-services-from-page.js). `bookTitle` is used when
  // the card's label differs from the bookable service's name.
  const handleBook = (service) => {
    const bookState = {
      service_id: service.id || null,
      service:    service.bookTitle || service.title,
    };
    const token = localStorage.getItem("token");
    if (!token) {
      // Not logged in → go to login, then come back to book
      navigate("/login", { state: { from:"/book-service", service, bookState } });
      return;
    }
    navigate("/book-service", { state: bookState });
  };

  return (
    <>
      <Header />

      <style>{`
        .sp-page *, .sp-page *::before, .sp-page *::after { box-sizing: border-box; }
        .sp-page { font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #374151; }

        /* ── Hero ──────────────────────────────────── */
        /* Banner has its heading/tagline baked into the image itself
           (designed graphic) — no HTML text overlay, same approach
           as the Contact and Plans page heroes. */
        .sp-hero { position: relative; overflow: hidden; width: 100%; }
        .sp-hero-full {
          width: 100%;
          aspect-ratio: 2048 / 520;
          background-image: url(${servicesHeroBanner});
          background-size: cover;
          background-position: center;
        }
        .sp-hero-mobile { display: none; }

        @media (max-width: 600px) {
          .sp-hero-full { display: none; }
          .sp-hero-mobile { display: block; }
          .sphm-text {
            width: 100%;
            aspect-ratio: 1150 / 520;
            background-image: url(${servicesHeroBanner});
            background-size: 178.09% auto;
            background-position: left top;
            background-repeat: no-repeat;
          }
          .sphm-photo {
            width: 100%;
            aspect-ratio: 898 / 520;
            background-image: url(${servicesHeroBanner});
            background-size: 228.06% auto;
            background-position: right top;
            background-repeat: no-repeat;
          }
        }

        /* ── How we help ────────────────────────────── */
        .sp-sec { background: #F8FAFC; padding: clamp(36px,5vw,72px) clamp(16px,5vw,72px); }
        .sp-sec-head { max-width: 640px; margin-bottom: 32px; }
        .sp-sec-title { font-family: inherit; font-size: clamp(20px,3.5vw,30px); font-weight: 800; color: #10243E; margin: 0 0 6px; }
        .sp-sec-sub   { color: #64748B; font-size: 14px; margin: 0; line-height: 1.6; }

        /* Directory layout: a scannable category rail beside the list,
           instead of icon-tile tabs sitting above it. */
        .sp-directory { display: grid; grid-template-columns: 240px 1fr; gap: 28px; align-items: start; }

        /* Desktop category rail */
        .sp-rail {
          display: flex; flex-direction: column; gap: 2px;
          position: sticky; top: 90px;
          background: #fff; border: 1px solid #E4E9F0; border-radius: 16px;
          padding: 8px;
        }
        .sp-rail-item {
          display: flex; align-items: center; gap: 12px; width: 100%;
          border: none; background: transparent;
          padding: 12px; border-radius: 10px;
          font-family: inherit; font-size: 13.5px; font-weight: 700; color: #4B5768;
          text-align: left; cursor: pointer; transition: background .16s, color .16s;
        }
        .sp-rail-item:hover { background: #EEF3FA; }
        .sp-rail-item.on    { background: #1F4E8C; color: #fff; }
        .sp-rail-ic  { flex-shrink: 0; display: flex; color: inherit; }
        .sp-rail-lbl { flex: 1; line-height: 1.3; }
        .sp-rail-count {
          flex-shrink: 0; font-size: 11px; font-weight: 800;
          padding: 2px 8px; border-radius: 20px;
          background: #EEF1F6; color: #64748B;
        }
        .sp-rail-item.on .sp-rail-count { background: rgba(255,255,255,.22); color: #fff; }

        /* Mobile category scroller — hidden on desktop */
        .sp-chipbar { display: none; }
        .sp-chip-track {
          display: flex; gap: 10px; overflow-x: auto; -webkit-overflow-scrolling: touch;
          padding: 2px 2px 4px; scrollbar-width: none;
        }
        .sp-chip-track::-webkit-scrollbar { display: none; }
        .sp-chip {
          flex-shrink: 0; display: flex; align-items: center; gap: 8px;
          padding: 10px 16px; border-radius: 999px;
          border: 1.5px solid #E4E9F0; background: #fff;
          font-family: inherit; font-size: 13px; font-weight: 700; color: #4B5768;
          white-space: nowrap; cursor: pointer; transition: all .16s;
        }
        .sp-chip.on   { background: #1F4E8C; border-color: #1F4E8C; color: #fff; }
        .sp-chip-ic   { display: flex; }

        /* Content pane */
        .sp-pane-head {
          display: flex; align-items: baseline; justify-content: space-between;
          gap: 12px; margin-bottom: 18px; padding-bottom: 14px;
          border-bottom: 1px solid #E4E9F0;
        }
        .sp-pane-head h3   { font-family: inherit; font-size: clamp(18px,2.4vw,22px); font-weight: 800; color: #10243E; margin: 0; }
        .sp-pane-count     { font-size: 12.5px; font-weight: 600; color: #8893A1; white-space: nowrap; }

        .sp-tickets { display: flex; flex-direction: column; gap: 14px; }
        .sp-ticket {
          background: #fff; border: 1px solid #E4E9F0; border-left: 3px solid #E4E9F0;
          border-radius: 14px; overflow: hidden;
          animation: spTicketIn .25s ease both;
          transition: border-color .18s, box-shadow .18s;
        }
        .sp-ticket:hover, .sp-ticket.expanded { border-left-color: #1F4E8C; box-shadow: 0 10px 26px rgba(16,36,62,.08); }
        @keyframes spTicketIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }

        /* Clickable row — toggles the detail panel below it */
        .sp-ticket-row {
          display: flex; justify-content: space-between; align-items: center; gap: 20px;
          padding: 20px 22px; cursor: pointer; position: relative;
        }
        .sp-ticket-row:focus-visible { outline: 2px solid #1F4E8C; outline-offset: -2px; }

        .sp-ticket-main { flex: 1; min-width: 0; }
        .sp-ticket-top  { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin-bottom: 6px; }
        .sp-ticket-top h4 { font-family: inherit; font-size: 17px; font-weight: 800; color: #10243E; margin: 0; }
        .sp-ticket-dur    { font-size: 12.5px; font-weight: 700; color: #8893A1; white-space: nowrap; }
        .sp-ticket-desc   { font-size: 13.5px; color: #5B6676; margin: 0 0 12px; line-height: 1.6; }
        .sp-ticket-tags   { display: flex; flex-wrap: wrap; gap: 8px; list-style: none; padding: 0; margin: 0; }
        .sp-ticket-tags li {
          font-size: 11.5px; font-weight: 700; color: #0F6B56;
          background: #EAF7F3; border: 1px solid #CFEEE4;
          padding: 4px 10px; border-radius: 7px;
        }

        .sp-ticket-side  { flex-shrink: 0; display: flex; flex-direction: column; align-items: flex-end; gap: 10px; }
        .sp-ticket-price { font-family: inherit; font-size: 21px; font-weight: 800; color: #F17A00; white-space: nowrap; }
        .sp-ticket-btn {
          background: #1F4E8C; color: #fff; border: none;
          padding: 10px 22px; border-radius: 10px; font-family: inherit;
          font-size: 13.5px; font-weight: 700; cursor: pointer; white-space: nowrap;
          transition: all .18s;
        }
        .sp-ticket-btn:hover { background: #16365e; transform: translateY(-1px); }
        .sp-ticket-btn.wide  { padding: 11px 26px; }

        .sp-ticket-chevron {
          flex-shrink: 0; width: 28px; height: 28px; border-radius: 50%;
          border: 1.5px solid #E4E9F0; display: flex; align-items: center; justify-content: center;
          color: #8893A1; transition: transform .22s, background .18s, border-color .18s, color .18s;
        }
        .sp-ticket.expanded .sp-ticket-chevron {
          transform: rotate(180deg); background: #1F4E8C; border-color: #1F4E8C; color: #fff;
        }

        /* Detail panel — animated open/close via grid-template-rows */
        .sp-ticket-expand { display: grid; grid-template-rows: 0fr; transition: grid-template-rows .28s ease; }
        .sp-ticket.expanded .sp-ticket-expand { grid-template-rows: 1fr; }
        .sp-ticket-expand-inner { overflow: hidden; }
        .sp-ticket-expand-content { padding: 2px 22px 22px; border-top: 1px solid #EEF1F6; margin: 0 22px; }

        .sp-detail-block { padding-top: 16px; }
        .sp-detail-block + .sp-detail-block { padding-top: 14px; }
        .sp-detail-block h5 { font-family: inherit; font-size: 12.5px; font-weight: 800; color: #10243E; margin: 0 0 8px; }
        .sp-detail-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 7px; }
        .sp-detail-list li { display: flex; align-items: flex-start; gap: 8px; font-size: 13.5px; color: #475569; line-height: 1.5; }
        .sp-detail-list li svg { flex-shrink: 0; margin-top: 2px; }
        .sp-detail-list.included li svg { color: #0F9D7C; }
        .sp-detail-list.notes li svg    { color: #1F4E8C; }

        .sp-price-tiers { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; }
        .sp-price-tiers li {
          display: flex; justify-content: space-between; align-items: center;
          padding: 8px 0; font-size: 13.5px; color: #475569;
          border-bottom: 1px dashed #E4E9F0;
        }
        .sp-price-tiers li:last-child { border-bottom: none; }
        .sp-tier-right { display: inline-flex; align-items: center; gap: 12px; }
        .sp-tier-btn {
          border: 1.5px solid #1F4E8C; background: #fff; color: #1F4E8C; cursor: pointer;
          font: inherit; font-size: 12.5px; font-weight: 700; padding: 5px 14px; border-radius: 999px;
          transition: background .15s, color .15s;
        }
        .sp-tier-btn:hover { background: #1F4E8C; color: #fff; }
        .sp-tier-note { font-size: 12px; color: #94A3B8; min-width: 52px; text-align: center; }
        .sp-price-tiers li strong { font-weight: 800; color: #F17A00; }

        .sp-ticket-expand-foot {
          display: flex; align-items: center; justify-content: space-between; gap: 16px;
          flex-wrap: wrap; margin-top: 18px; padding-top: 16px; border-top: 1px solid #EEF1F6;
        }
        .sp-ticket-expand-meta { display: flex; gap: 28px; }
        .sp-ticket-expand-meta span { font-size: 13px; color: #475569; line-height: 1.5; }
        .sp-ticket-expand-meta strong { display: block; font-size: 11px; font-weight: 700; color: #8893A1; margin-bottom: 2px; }

        /* Most booked section */
        .sp-mb-sec { background: #fff; padding: clamp(36px,5vw,72px) clamp(16px,5vw,72px); }
        .sp-mb-head { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 28px; flex-wrap: wrap; gap: 12px; }
        .sp-mb-title { font-family: inherit; font-size: clamp(20px,3.5vw,30px); font-weight: 800; color: #1E293B; margin: 0; }
        .sp-mb-title em { font-style: italic; color: #F39C12; }
        .sp-mb-arrows { display: flex; gap: 8px; }
        .sp-bprev, .sp-bnext {
          width: 38px; height: 38px; border-radius: 50%; border: 1.5px solid #E2E8F0;
          background: #fff; cursor: pointer; display: flex; align-items: center;
          justify-content: center; font-size: 18px; transition: all .2s; color: #64748B;
        }
        .sp-bprev:hover, .sp-bnext:hover { background: #1F4E8C; border-color: #1F4E8C; color: #fff; }

        /* Cards */
        .sp-card {
          background: #fff; border-radius: 16px; overflow: hidden;
          border: 1px solid #E2E8F0; transition: all .22s;
          box-shadow: 0 2px 8px rgba(0,0,0,.05); height: 100%;
        }
        .sp-card:hover { transform: translateY(-5px); box-shadow: 0 12px 28px rgba(31,78,140,.13); border-color: #BFDBFE; }
        .sp-card img { width: 100%; height: 155px; object-fit: cover; display: block; }
        .sp-card-body { padding: 14px; text-align: center; }
        .sp-card-body h3 { font-family: inherit; font-size: 14px; font-weight: 700; color: #1E293B; margin: 0 0 12px; }
        .sp-card-btn {
          background: #1F4E8C; color: #fff; border: none;
          padding: 9px 18px; border-radius: 10px; font-size: 13px;
          font-weight: 700; cursor: pointer; width: 100%;
          font-family: inherit; transition: background .2s;
        }
        .sp-card-btn:hover { background: #1a4279; }

        /* Responsive */
        @media (max-width: 860px) {
          .sp-directory { grid-template-columns: 1fr; gap: 0; }
          .sp-rail    { display: none; }
          .sp-chipbar { display: block; margin-bottom: 24px; }
        }
        @media (max-width: 620px) {
          .sp-ticket-row  { flex-direction: column; align-items: flex-start; padding-right: 54px; }
          .sp-ticket-side { flex-direction: row; align-items: center; justify-content: space-between; width: 100%; }
          .sp-ticket-chevron { position: absolute; top: 20px; right: 22px; }
          .sp-ticket-expand-content { margin: 0 16px; padding: 2px 6px 18px; }
          .sp-ticket-expand-foot { flex-direction: column; align-items: stretch; }
          .sp-ticket-btn.wide { width: 100%; }
        }
        @media (max-width: 480px) {
          .sp-mb-head { flex-direction: column; align-items: flex-start; }
          .sp-sec-head { margin-bottom: 24px; }
          .sp-pane-head { flex-wrap: wrap; row-gap: 4px; }
          .sp-ticket-top { flex-wrap: wrap; row-gap: 4px; }
          .sp-ticket-price { font-size: 19px; }
          .sp-chip { padding: 9px 14px; font-size: 12.5px; }
        }
      `}</style>

      <div className="sp-page">

        {/* ── HERO ── */}
        <div
          className="sp-hero"
          role="img"
          aria-label="Our Services. From healthcare to everyday support — all in one place."
        >
          <div className="sp-hero-full" />
          <div className="sp-hero-mobile">
            <div className="sphm-text" />
            <div className="sphm-photo" />
          </div>
        </div>

        {/* ── HOW WE HELP ── */}
        <section className="sp-sec" id="how-we-help">
          <div className="sp-sec-head">
            <h2 className="sp-sec-title">How we help</h2>
            <p className="sp-sec-sub">Six ways we support care at home — pick one to see what's included.</p>
          </div>

          <div className="sp-directory">

            {/* Desktop — vertical category rail */}
            <nav className="sp-rail" aria-label="Service categories">
              {tabs.map(t => (
                <button
                  key={t.id}
                  type="button"
                  className={`sp-rail-item${active===t.id?" on":""}`}
                  onClick={()=>selectCategory(t.id)}
                >
                  <span className="sp-rail-ic">{ICONS[t.id]}</span>
                  <span className="sp-rail-lbl">{t.name}</span>
                  <span className="sp-rail-count">{(servicesData[t.id]||[]).length}</span>
                </button>
              ))}
            </nav>

            {/* Mobile — horizontal category scroller */}
            <div className="sp-chipbar">
              <div className="sp-chip-track">
                {tabs.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    className={`sp-chip${active===t.id?" on":""}`}
                    onClick={()=>selectCategory(t.id)}
                  >
                    <span className="sp-chip-ic">{ICONS[t.id]}</span>
                    {t.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Services for the selected category */}
            <div className="sp-pane">
              <div className="sp-pane-head">
                <h3>{tabs.find(t=>t.id===active)?.name}</h3>
                <span className="sp-pane-count">
                  {(servicesData[active]||[]).length} service{(servicesData[active]||[]).length===1?"":"s"}
                </span>
              </div>

              <div className="sp-tickets">
                {(servicesData[active]||[]).map((s,i) => {
                  const key = `${active}-${i}`;
                  const isOpen = expanded === key;
                  return (
                    <article
                      className={`sp-ticket${isOpen ? " expanded" : ""}`}
                      key={key}
                      style={{ animationDelay:`${i*.06}s` }}
                    >
                      <div
                        className="sp-ticket-row"
                        role="button"
                        tabIndex={0}
                        aria-expanded={isOpen}
                        onClick={()=>toggleExpand(key)}
                        onKeyDown={e=>{ if(e.key==="Enter"||e.key===" "){ e.preventDefault(); toggleExpand(key); } }}
                      >
                        <div className="sp-ticket-main">
                          <div className="sp-ticket-top">
                            <h4>{s.title}</h4>
                            <span className="sp-ticket-dur">{s.duration}</span>
                          </div>
                          <p className="sp-ticket-desc">{s.desc}</p>
                          <ul className="sp-ticket-tags">
                            {s.points.map((p,j)=><li key={j}>{p}</li>)}
                          </ul>
                        </div>
                        <div className="sp-ticket-side">
                          <span className="sp-ticket-price">{s.price}</span>
                          <button className="sp-ticket-btn" onClick={(e)=>{ e.stopPropagation(); handleBook(s); }}>
                            Book →
                          </button>
                        </div>
                        <span className="sp-ticket-chevron"><Chevron /></span>
                      </div>

                      <div className="sp-ticket-expand">
                        <div className="sp-ticket-expand-inner">
                          <div className="sp-ticket-expand-content">

                            {s.priceTiers && (
                              <div className="sp-detail-block">
                                <h5>Pricing</h5>
                                <ul className="sp-price-tiers">
                                  {s.priceTiers.map((t,j)=>(
                                    <li key={j}>
                                      <span>{t.label}</span>
                                      <span className="sp-tier-right">
                                        <strong>{t.price}</strong>
                                        {t.book
                                          ? <button type="button" className="sp-tier-btn" onClick={(e)=>{ e.stopPropagation(); handleBook({ title:s.title, bookTitle:t.book }); }}>Book</button>
                                          : <span className="sp-tier-note">Call us</span>}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            <div className="sp-detail-block">
                              <h5>What's included</h5>
                              <ul className="sp-detail-list included">
                                {s.points.map((p,j)=><li key={j}><CheckDot />{p}</li>)}
                              </ul>
                            </div>

                            <div className="sp-detail-block">
                              <h5>Good to know</h5>
                              <ul className="sp-detail-list notes">
                                {(CATEGORY_NOTES[active]||[]).map((n,j)=><li key={j}><InfoDot />{n}</li>)}
                              </ul>
                            </div>

                            <div className="sp-ticket-expand-foot">
                              <div className="sp-ticket-expand-meta">
                                <span><strong>Duration</strong>{s.duration}</span>
                                <span><strong>Price</strong>{s.price}</span>
                              </div>
                              <button className="sp-ticket-btn wide" onClick={(e)=>{ e.stopPropagation(); handleBook(s); }}>
                                Book this service →
                              </button>
                            </div>

                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

          </div>
        </section>

        {/* ── MOST BOOKED ── */}
        <section className="sp-mb-sec">
          <div className="sp-mb-head">
            <h2 className="sp-mb-title">Most <em>booked</em> services</h2>
            <div className="sp-mb-arrows">
              <button className="sp-bprev">‹</button>
              <button className="sp-bnext">›</button>
            </div>
          </div>

          <Swiper modules={[Navigation]}
            navigation={{ prevEl:".sp-bprev", nextEl:".sp-bnext" }}
            spaceBetween={14} slidesPerView={1}
            breakpoints={{
              480:{ slidesPerView:2, spaceBetween:12 },
              768:{ slidesPerView:3, spaceBetween:14 },
              1024:{ slidesPerView:4, spaceBetween:16 },
              1280:{ slidesPerView:5, spaceBetween:18 },
            }}>
            {bookedServices.map((s,i) => (
              <SwiperSlide key={i} style={{ height:"auto" }}>
                <div className="sp-card">
                  <img src={s.image} alt={s.title} loading="lazy" />
                  <div className="sp-card-body">
                    <h3>{s.title}</h3>
                    <button className="sp-card-btn" onClick={() => handleBook({ title:s.title, bookTitle:s.book || s.title })}>
                      Book Now
                    </button>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </section>

      </div>
      <Footer />
    </>
  );
}